import type { Rng } from '../content/types';
import { roundTo } from '../math/format';

/**
 * Seeded PRNG (mulberry32). Deterministic on purpose: a set built from a seed
 * can be rebuilt identically, so "park it" can bring back the exact same
 * question tomorrow rather than a lookalike.
 */
export function createRng(seed: number | string): Rng {
  let state = typeof seed === 'number' ? seed >>> 0 : hashString(seed);

  const next = (): number => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  const int = (min: number, max: number): number => min + Math.floor(next() * (max - min + 1));

  return {
    next,
    int,
    pick<T>(items: readonly T[]): T {
      if (items.length === 0) throw new Error('pick() from an empty list');
      return items[int(0, items.length - 1)]!;
    },
    shuffle<T>(items: readonly T[]): T[] {
      const out = [...items];
      for (let i = out.length - 1; i > 0; i--) {
        const j = int(0, i);
        [out[i], out[j]] = [out[j]!, out[i]!];
      }
      return out;
    },
    step(min: number, max: number, step: number): number {
      const steps = Math.floor((max - min) / step);
      // Round to kill the float dust that 0.1 * 3 style arithmetic leaves behind.
      return roundTo(min + int(0, steps) * step, 6);
    },
  };
}

export function hashString(value: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < value.length; i++) {
    h ^= value.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
